import { hasSessionStorage, hasLocalStorage } from '@gig/magic-common';

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const LAST_PROMPT_TIME = "magic:rc-prompt-time";
const NEW_INTERVAL = "magic:rc-new-interval";
const STOP = "stop";
const DEFAULT_INTERVAL_MINUTES = 60;
const DEFAULT_INTERVAL_TIME = DEFAULT_INTERVAL_MINUTES * 60 * 1e3;
class RealityCheckService {
  constructor(isLoggedIn = false, lastLoginTime = null, userInterval = DEFAULT_INTERVAL_TIME) {
    this.listeners = [];
    this.sessionChangedHandlers = [];
    this.isStarted = false;
    this.timesShown = 0;
    this.destructors = [];
    this.timer = null;
    this.isLoggedIn = isLoggedIn;
    this.loginTime = lastLoginTime;
    this.userInterval = userInterval;
  }
  onRealityCheckInterval(listener, loginTimestamp) {
    if (!this.isStarted) {
      this.isStarted = true;
      this.startRealityCheckInterval(loginTimestamp);
    }
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index === -1) {
        return;
      }
      this.listeners.splice(index, 1);
      if (this.listeners.length === 0) {
        this.destroy();
      }
    };
  }
  destroy(clearSessionData = true) {
    this.listeners = [];
    this.isStarted = false;
    this.isLoggedIn = false;
    this.clearTimers();
    this.destructors.forEach((destruct) => destruct());
    this.destructors = [];
    if (clearSessionData) {
      this.timesShown = 0;
      if (hasSessionStorage()) {
        sessionStorage.removeItem(LAST_PROMPT_TIME);
      }
      if (hasLocalStorage()) {
        localStorage.removeItem(NEW_INTERVAL);
      }
    }
  }
  stopInterval(clearStorage = true) {
    this.clearTimers();
    if (clearStorage && hasLocalStorage()) {
      localStorage.setItem(NEW_INTERVAL, STOP);
    }
  }
  sessionChanged(isLoggedIn, lastLoginTime, interval) {
    if (this.isLoggedIn === isLoggedIn) {
      return;
    }
    this.isLoggedIn = isLoggedIn;
    if (isLoggedIn && lastLoginTime) {
      this.loginTime = lastLoginTime;
    }
    if (interval) {
      this.userInterval = interval;
    }
    this.sessionChangedHandlers.forEach((handler) => {
      try {
        handler(isLoggedIn);
      } catch (e) {
        console.error("Error in Reality Check Session Changed Handler:", e);
      }
    });
  }
  onSessionChange(handler) {
    this.sessionChangedHandlers.push(handler);
    return () => {
      const index = this.sessionChangedHandlers.indexOf(handler);
      if (index === -1) {
        return;
      }
      this.sessionChangedHandlers.splice(index, 1);
    };
  }
  onLoginChange(handler) {
    let currentStatus = null;
    if (currentStatus !== this.isLoggedIn) {
      handler(this.isLoggedIn);
      currentStatus = this.isLoggedIn;
    }
    this.destructors.push(this.onSessionChange((isLoggedIn) => {
      handler(isLoggedIn);
    }));
  }
  shouldEmitPrompt() {
    if (!hasSessionStorage()) {
      return true;
    }
    const lastTime = sessionStorage.getItem(LAST_PROMPT_TIME);
    if (!lastTime) {
      return true;
    }
    console.warn("shouldEmitPrompt", +lastTime > Date.now());
    return +lastTime > Date.now();
  }
  onRealityIntervalChange() {
    if (!hasLocalStorage()) {
      return;
    }
    const handler = (event) => {
      if (event.key === NEW_INTERVAL && event.newValue) {
        try {
          if (event.newValue === STOP) {
            this.stopInterval(false);
            return;
          }
          const interval = parseInt(event.newValue || "", 10);
          if (this.loginTime) {
            this.startTimer(interval);
          }
        } catch (e) {
          console.error("Error receiving new rg interval", e);
        }
      }
    };
    window.addEventListener("storage", handler);
    this.destructors.push(() => {
      window.removeEventListener("storage", handler);
    });
  }
  startRealityCheckInterval(loginTimeProvided) {
    this.isStarted = true;
    this.onRealityIntervalChange();
    this.onLoginChange((isLoggedIn) => {
      console.warn("isLoggedIn", isLoggedIn);
      if (isLoggedIn) {
        this.loginTime = loginTimeProvided ? loginTimeProvided : this.loginTime;
        this.startTimer(this.userInterval);
      } else {
        this.clearTimers();
        this.loginTime = null;
        this.timesShown = 0;
        if (hasSessionStorage()) {
          sessionStorage.removeItem(LAST_PROMPT_TIME);
        }
      }
    });
  }
  clearTimers() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }
  startTimer(interval) {
    this.clearTimers();
    if (!this.loginTime) {
      throw new Error("Reality Check interval started without login time being set.");
    }
    const timeout = interval - (Date.now() - this.loginTime) % interval;
    return new Promise((resolve, reject) => {
      this.timer = setTimeout(() => {
        const shouldEmit = this.shouldEmitPrompt();
        const promises = [this.startTimer(interval)];
        if (shouldEmit) {
          promises.push(this.emitIntervalElapsed());
        }
        Promise.all(promises).then(() => resolve(), reject);
      }, timeout);
    });
  }
  emitIntervalElapsed() {
    return __async(this, null, function* () {
      this.timesShown++;
      this.listeners.forEach((listener) => {
        try {
          listener({
            timesShown: this.timesShown,
            loginTimestamp: this.loginTime
          });
        } catch (e) {
          console.error("Error in Reality Check Prompt Listener:", e);
        }
      });
    });
  }
}
let REALITY_CHECK_INSTANCE;
const getRealityCheckApi = (isLoggedIn, lastLoginTime, userInterval) => {
  if (!REALITY_CHECK_INSTANCE) {
    REALITY_CHECK_INSTANCE = new RealityCheckService(isLoggedIn, lastLoginTime, userInterval);
  } else {
    REALITY_CHECK_INSTANCE.sessionChanged(isLoggedIn, lastLoginTime, userInterval);
  }
  return REALITY_CHECK_INSTANCE;
};

export { DEFAULT_INTERVAL_MINUTES, DEFAULT_INTERVAL_TIME, LAST_PROMPT_TIME, NEW_INTERVAL, RealityCheckService, getRealityCheckApi };
